import { FC, memo, useEffect, useRef, useState } from "react";
import { Field } from "react-final-form";

import { ITextField, Stack } from "@bps/fluent-ui";
import { CommunicationType } from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { CommunicationFieldValue } from "@shared-types/practice/communication-field-value.type.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { Contact } from "@stores/practice/models/Contact.ts";
import { Fieldset } from "@ui-components/form/Fieldset/Fieldset.tsx";
import { FieldSpy } from "@ui-components/form/FieldSpy.tsx";
import { PhoneTextField } from "@ui-components/form/PhoneTextField.tsx";
import {
  TextFieldActionButtonProps,
  TextInputField
} from "@ui-components/form/TextInputField.tsx";
import { ToggleField } from "@ui-components/form/Toggle/ToggleField.tsx";

import { Labels } from "../../../../shared-components/types/labels.enums.types.ts";
import { isCommunicationTypePhone } from "../../view/contact-method-list/utils.ts";
import { patientFormNameOf } from "../PatientEditFormValues.tsx";
import { SendFormDialog } from "../SendFormDialog.tsx";
import { DuplicatedMobileMessageBar } from "./DuplicatedMobileMessageBar.tsx";
import { RemoveContactMethodLink } from "./RemoveContactMethodLink.tsx";
import { SendFormButton } from "./SendFormButton.tsx";

interface CommunicationFieldProps {
  value: CommunicationFieldValue;
  onRemove: () => void;
  index: number;
  name: string;
  contact: Contact | undefined;
  required?: boolean;
  disableRemoveLink?: boolean;
  hideSendForm?: boolean;
  disablePreferredChoice?: boolean;
  onChangePreferred: (value: boolean) => void;
}

export const CommunicationField: FC<CommunicationFieldProps> = memo(
  ({
    name,
    index,
    value,
    contact,
    required,
    onRemove,
    hideSendForm,
    onChangePreferred,
    disableRemoveLink,
    disablePreferredChoice
  }) => {
    const { core } = useStores();

    const [sendDialogVisible, setSendDialogVisible] = useState(false);

    const communicationRef = useRef<ITextField | null>(null);
    useEffect(() => {
      if (communicationRef.current && value.isNew) {
        communicationRef.current.focus();
      }
    }, [value.isNew]);

    const actionButtonProp: TextFieldActionButtonProps | undefined =
      !disableRemoveLink
        ? {
            linkProps: { onClick: () => onRemove(), children: Labels.remove }
          }
        : undefined;

    const isPhoneType = isCommunicationTypePhone(value.type);

    return (
      <Fieldset
        frame
        horizontalAlign="space-between"
        verticalAlign="end"
        tokens={{ childrenGap: 16 }}
        styles={{ root: { marginBottom: 16 } }}
      >
        <Stack tokens={{ childrenGap: 8 }}>
          <Stack horizontal tokens={{ childrenGap: 8 }}>
            {isPhoneType ? (
              <PhoneTextField
                componentRef={(ref: ITextField) => {
                  communicationRef.current = ref;
                }}
                label={core.ref.contactCommTypes.get(value.type)?.text}
                required={required}
                styles={{
                  root: {
                    width: "100%"
                  }
                }}
                actionButton={
                  disablePreferredChoice ? actionButtonProp : undefined
                }
                maxLength={15}
                name={`${name}[${index}].value`}
              />
            ) : (
              <>
                <TextInputField
                  componentRef={ref => {
                    communicationRef.current = ref;
                  }}
                  required={required}
                  label={core.ref.contactCommTypes.get(value.type)?.text}
                  actionButton={
                    value.type !== CommunicationType.Email ||
                    (hideSendForm && value.type === CommunicationType.Email)
                      ? actionButtonProp
                      : undefined
                  }
                  styles={{ root: { width: "100%" } }}
                  name={`${name}[${index}].value`}
                />

                {!hideSendForm && value.type === CommunicationType.Email && (
                  <Stack>
                    <RemoveContactMethodLink
                      index={index}
                      onRemove={onRemove}
                    />
                    <SendFormButton
                      setSendDialogVisible={setSendDialogVisible}
                      value={value.value}
                      styles={{ root: { marginTop: 1 } }}
                    />
                  </Stack>
                )}
              </>
            )}
            {!hideSendForm && value.type === CommunicationType.Mobile && (
              <SendFormButton
                setSendDialogVisible={setSendDialogVisible}
                value={value.value}
                styles={{ root: { marginTop: 29 } }}
              />
            )}
            {!disablePreferredChoice && isPhoneType && (
              <Stack tokens={{ childrenGap: 8 }}>
                <RemoveContactMethodLink index={index} onRemove={onRemove} />
                <ToggleField
                  id={`comm-pref-toggle-${index}`}
                  className="communications-toggle"
                  name={`${name}[${index}].preferred`}
                  onText={Labels.preferred}
                  offText={Labels.preferred}
                  styles={{
                    root: { marginBottom: 4 },
                    label: index !== 0 && { visibility: "hidden" }
                  }}
                />
                <FieldSpy
                  name={`${name}[${index}].preferred`}
                  onChange={(value: boolean) => onChangePreferred(value)}
                />
                <Field
                  type="hidden"
                  name={patientFormNameOf("tempFormAddressValue")}
                >
                  {({ input }) => <input {...input} />}
                </Field>
                <Field
                  type="hidden"
                  name={patientFormNameOf("tempFormChannelType")}
                >
                  {({ input }) => <input {...input} />}
                </Field>
              </Stack>
            )}
          </Stack>
          <DuplicatedMobileMessageBar value={value} contact={contact} />
        </Stack>
        {sendDialogVisible && (
          <SendFormDialog
            onDismiss={() => setSendDialogVisible(false)}
            value={value}
          />
        )}
      </Fieldset>
    );
  }
);
